/**
 * Permission codes the Inventory screens declare an interest in.
 *
 * They are the dot-strings `internal/inventory/permissions.go` registers with
 * the RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them
 * once here means a renamed permission is a compile error rather than a menu
 * item that silently disappears.
 *
 * Two of them are a **declared segregation-of-duties conflict**:
 * `inventory.adjustment.create` and `inventory.adjustment.approve`. The same
 * person must not both raise a stock write-off and post it into the ledger, so
 * the adjustment screens check them separately and never treat "can raise" as
 * "can approve" — see `AdjustmentView.vue`.
 *
 * The block below the module's own codes is what this module reads *other*
 * modules through. An inventory figure is only ever half of the story: the
 * journals every movement posts belong to GL, the accounts a valuation
 * reconciles against and the companies everything is scoped to belong to the
 * organization module. A user who may read the stock ledger but not the general
 * ledger sees the ledger and is told what they cannot follow, rather than being
 * handed a link that 403s.
 */

export const INVENTORY_PERMISSIONS = {
  itemRead: 'inventory.item.read',
  itemManage: 'inventory.item.manage',

  warehouseRead: 'inventory.warehouse.read',
  warehouseManage: 'inventory.warehouse.manage',

  stockRead: 'inventory.stock.read',
  stockReceive: 'inventory.stock.receive',
  stockIssue: 'inventory.stock.issue',
  stockTransfer: 'inventory.stock.transfer',
  stockRevalue: 'inventory.stock.revalue',

  adjustmentCreate: 'inventory.adjustment.create',
  adjustmentApprove: 'inventory.adjustment.approve',

  cycleCountManage: 'inventory.cyclecount.manage',
  cycleCountPost: 'inventory.cyclecount.post',

  reservationManage: 'inventory.reservation.manage',

  reportRead: 'inventory.report.read',

  /* --------------------------------------------------------------- C6 (§6.1) */

  /**
   * Lot codes. `lot.read` is held by the sales roles so an order screen can show
   * which lot was allocated — which is why `StockLotDTO` **omits**
   * `supplierLotRef` and `vendorId` for a caller without `lot.manage`. A
   * field-level omission, not a 403, so the screen still renders (§6.11).
   */
  lotRead: 'inventory.lot.read',
  lotManage: 'inventory.lot.manage',
  /** Quarantine / block / release. A storekeeper physically quarantines a
   * damaged pallet, so `warehouse_officer` holds this and not `lot.manage`. */
  lotStatus: 'inventory.lot.status',

  recallRead: 'inventory.recall.read',
  recallManage: 'inventory.recall.manage',
  /**
   * The containment act itself — the one that stops stock moving across every
   * warehouse. **MFA step-up**, and deliberately NOT granted to
   * `inventory_manager`: it is the second hand of a declared pair.
   */
  recallQuarantine: 'inventory.recall.quarantine',

  zoneRead: 'inventory.zone.read',
  zoneManage: 'inventory.zone.manage',

  settingsRead: 'inventory.settings.read',
  settingsManage: 'inventory.settings.manage',

  /** Despatch reuses `inventory.stock.transfer` for read; these are the acts. */
  transferDespatch: 'inventory.transfer.despatch',
  transferReceive: 'inventory.transfer.receive',

  /**
   * Held by `RoleEmployee`, which is why the requisition surface is **row
   * scoped**: an outlet cook may read requisitions, and may not read another
   * outlet's. The server returns 404, never 403, for a row outside scope —
   * 403 would tell the caller the id exists (§6.12).
   */
  requisitionRead: 'inventory.requisition.read',
  requisitionCreate: 'inventory.requisition.create',
  /** Declared SoD conflict against `requisition.create`. */
  requisitionApprove: 'inventory.requisition.approve',

  parRead: 'inventory.par.read',
  parManage: 'inventory.par.manage',

  /**
   * `waste.record` and `waste.approve` are a declared segregation-of-duties
   * pair. A bartender who can both write off a bottle and approve the write-off
   * has no control at all, so the screens check them separately and never treat
   * "can record" as "can approve".
   */
  wasteRecord: 'inventory.waste.record',
  wasteApprove: 'inventory.waste.approve',

  recipeRead: 'inventory.recipe.read',
  recipeManage: 'inventory.recipe.manage',
  /** The permission a POS terminal's service account holds at settlement. */
  depletionRun: 'inventory.depletion.run',

  landedCostRead: 'inventory.landed_cost.read',
  landedCostManage: 'inventory.landed_cost.manage',
  /**
   * **MFA step-up**, and the second hand of a declared pair against
   * `landed_cost.manage`: whoever decides which charges attach to which receipt
   * lines must not be the one who puts the answer into the carrying value.
   */
  landedCostAbsorb: 'inventory.landed_cost.absorb',

  replenishmentManage: 'inventory.replenishment.manage',

  haccpRead: 'inventory.haccp.read',
  haccpRecord: 'inventory.haccp.record',

  reconRead: 'inventory.recon.read',
  reconClear: 'inventory.recon.clear',

  /** Scope every screen to a company, and name the one a warehouse belongs to. */
  companyRead: 'org.company.read',
  /** The chart of accounts behind an item's three per-company posting accounts. */
  accountsRead: 'org.account.read',
  /** The journals every movement, adjustment, transfer and count posted. */
  journalRead: 'gl.journal.read',
} as const

export type InventoryPermission =
  (typeof INVENTORY_PERMISSIONS)[keyof typeof INVENTORY_PERMISSIONS]
